"use client"

import { useEffect, useRef, useState } from "react"
import AssetComboIcon from "../DefuseSDK/components/Asset/AssetComboIcon"

type Candle = {
  open: number
  high: number
  low: number
  close: number
}

const CANDLE_REVEAL_MS = 25
const CANDLE_ENTER_MS = 220
const PRICE_LABEL_DELAY_MS = CANDLE_ENTER_MS - 100
const CHART_WIDTH = 320
const CHART_HEIGHT = 208
const AXIS_WIDTH = 62
const PRICE_LABELS = [61_000, 62_000, 63_000]

const candles: Candle[] = [
  { open: 60958, high: 61042, low: 60832, close: 60905 },
  { open: 60905, high: 61034, low: 60733, close: 60935 },
  { open: 60935, high: 61037, low: 60882, close: 60882 },
  { open: 60882, high: 61349, low: 60873, close: 61254 },
  { open: 61254, high: 61567, low: 61182, close: 61441 },
  { open: 61441, high: 61643, low: 61333, close: 61611 },
  { open: 61611, high: 61873, low: 61539, close: 61774 },
  { open: 61774, high: 61870, low: 61370, close: 61470 },
  { open: 61470, high: 61595, low: 61399, close: 61495 },
  { open: 61495, high: 61899, low: 61381, close: 61845 },
  { open: 61845, high: 62189, low: 61789, close: 62103 },
  { open: 62103, high: 62493, low: 61949, close: 62314 },
  { open: 62314, high: 62554, low: 62236, close: 62543 },
  { open: 62543, high: 62844, low: 62382, close: 62764 },
  { open: 62764, high: 62994, low: 62665, close: 62971 },
  { open: 62971, high: 63110, low: 62912, close: 62984 },
  { open: 62984, high: 63085, low: 62916, close: 63047 },
  { open: 63047, high: 63309, low: 62881, close: 63151 },
  { open: 63151, high: 63206, low: 63000, close: 63026 },
  { open: 63026, high: 63058, low: 62528, close: 62627 },
  { open: 62627, high: 63085, low: 62598, close: 62978 },
  { open: 62978, high: 63098, low: 62937, close: 62987 },
  { open: 62987, high: 63048, low: 62757, close: 62884 },
  { open: 62884, high: 63018, low: 62626, close: 62736 },
  { open: 62736, high: 62888, low: 62336, close: 62447 },
  { open: 62447, high: 62521, low: 62091, close: 62260 },
  { open: 62260, high: 62286, low: 62169, close: 62178 },
  { open: 62178, high: 62447, low: 62148, close: 62381 },
  { open: 62381, high: 62620, low: 62311, close: 62584 },
  { open: 62584, high: 62933, low: 62583, close: 62901 },
  { open: 62901, high: 62957, low: 62658, close: 62668 },
  { open: 62668, high: 62676, low: 62349, close: 62396 },
  { open: 62396, high: 62696, low: 62374, close: 62625 },
  { open: 62625, high: 62847, low: 62620, close: 62726 },
  { open: 62726, high: 62910, low: 62589, close: 62892 },
  { open: 62892, high: 63147, low: 62752, close: 63072 },
  { open: 63072, high: 63283, low: 63063, close: 63211 },
  { open: 63211, high: 63317, low: 63096, close: 63296 },
  { open: 63296, high: 63322, low: 63033, close: 63170 },
  { open: 63170, high: 63175, low: 62662, close: 62837 },
  { open: 62837, high: 63255, low: 62813, close: 63145 },
  { open: 63145, high: 63294, low: 63082, close: 63191 },
  { open: 63191, high: 63673, low: 63117, close: 63582 },
  { open: 63582, high: 63913, low: 63429, close: 63739 },
  { open: 63739, high: 63874, low: 63628, close: 63660 },
  { open: 63660, high: 63765, low: 63544, close: 63750 },
  { open: 63750, high: 63879, low: 63577, close: 63621 },
  { open: 63621, high: 63940, low: 63570, close: 63890 },
]

const CANDLE_COUNT = candles.length
const START_PRICE = candles[0].open
const FINAL_PRICE = candles[CANDLE_COUNT - 1].close
const PRICE_CHANGE = FINAL_PRICE - START_PRICE
const PRICE_CHANGE_PCT = (PRICE_CHANGE / START_PRICE) * 100
const priceMin = Math.min(...candles.map((candle) => candle.low)) - 40
const priceMax = Math.max(...candles.map((candle) => candle.high)) + 280
const currentPriceY =
  ((priceMax - FINAL_PRICE) / (priceMax - priceMin)) * CHART_HEIGHT

function formatUsd(price: number) {
  return `$${price.toLocaleString("en-US")}`
}

function priceToY(price: number) {
  return ((priceMax - price) / (priceMax - priceMin)) * CHART_HEIGHT
}

function CandleStick({
  candle,
  index,
  visible,
}: {
  candle: Candle
  index: number
  visible: boolean
}) {
  const isUp = candle.close >= candle.open
  const color = isUp ? "#00C076" : "#f43f5e"
  const slotWidth = CHART_WIDTH / CANDLE_COUNT
  const x = index * slotWidth + slotWidth / 2
  const bodyTop = priceToY(Math.max(candle.open, candle.close))
  const bodyBottom = priceToY(Math.min(candle.open, candle.close))
  const bodyHeight = Math.max(bodyBottom - bodyTop, 1.5)
  const wickTop = priceToY(candle.high)
  const wickBottom = priceToY(candle.low)
  const bodyWidth = Math.max(slotWidth * 0.72, 3)

  return (
    <g
      style={{
        opacity: visible ? 1 : 0,
        transform: visible ? "scaleY(1)" : "scaleY(0.35)",
        transformOrigin: `${x}px ${wickBottom}px`,
        transition: "opacity 180ms ease-out, transform 220ms ease-out",
      }}
    >
      <line
        x1={x}
        x2={x}
        y1={wickTop}
        y2={wickBottom}
        stroke={color}
        strokeWidth={1.25}
        strokeLinecap="round"
      />
      <rect
        x={x - bodyWidth / 2}
        y={bodyTop}
        width={bodyWidth}
        height={bodyHeight}
        rx={1}
        fill={color}
      />
    </g>
  )
}

const TradingIllustration = ({ isActive = true }: { isActive?: boolean }) => {
  const rootRef = useRef<HTMLDivElement>(null)
  const [isInView, setIsInView] = useState(false)
  const [hasStarted, setHasStarted] = useState(false)
  const [visibleCount, setVisibleCount] = useState(0)
  const [showPriceLabel, setShowPriceLabel] = useState(false)

  useEffect(() => {
    const element = rootRef.current
    if (!element) {
      return
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting) {
          setIsInView(true)
        }
      },
      { threshold: 0.4 }
    )

    observer.observe(element)
    return () => observer.disconnect()
  }, [])

  useEffect(() => {
    if (isActive && isInView) {
      setHasStarted(true)
    }
  }, [isActive, isInView])

  useEffect(() => {
    if (!hasStarted) {
      return
    }

    setVisibleCount(0)
    setShowPriceLabel(false)

    let count = 0
    let labelTimeout: ReturnType<typeof setTimeout> | undefined

    const interval = setInterval(() => {
      count += 1
      setVisibleCount(count)
      if (count >= CANDLE_COUNT) {
        clearInterval(interval)
        labelTimeout = setTimeout(
          () => setShowPriceLabel(true),
          PRICE_LABEL_DELAY_MS
        )
      }
    }, CANDLE_REVEAL_MS)

    return () => {
      clearInterval(interval)
      if (labelTimeout) {
        clearTimeout(labelTimeout)
      }
    }
  }, [hasStarted])

  return (
    <div
      ref={rootRef}
      className="flex w-full max-w-[min(85vw,26rem)] flex-col overflow-hidden rounded-2xl border border-white/5 bg-gray-900"
    >
      <div className="flex items-center gap-3 p-5 pb-4">
        <AssetComboIcon
          icon="/static/icons/token/btc.svg"
          sizeClassName="size-12"
        />
        <div className="flex flex-col">
          <div className="font-semibold text-base/5 text-white tracking-tight">
            BTC
          </div>
          <div className="font-medium text-gray-400 text-sm/5">Bitcoin</div>
        </div>
      </div>

      <div className="flex flex-col gap-2">
        <div className="flex items-baseline gap-2.5 px-5">
          <div className="font-semibold text-2xl/7 text-white tracking-tight">
            {formatUsd(FINAL_PRICE)}
          </div>
          <div className="font-semibold text-green-500 text-sm/5 tracking-tight">
            {`+$${PRICE_CHANGE.toLocaleString("en-US")}`}
          </div>
          <div className="font-semibold text-green-500 text-sm/5 tracking-tight">
            +{PRICE_CHANGE_PCT.toFixed(2)}%
          </div>
        </div>

        <div className="relative">
          <div className="absolute inset-y-0 w-4 bg-linear-to-r from-gray-900 to-gray-900/0" />
          <svg
            viewBox={`0 0 ${CHART_WIDTH + AXIS_WIDTH} ${CHART_HEIGHT}`}
            className="w-full"
            style={{
              aspectRatio: `${CHART_WIDTH + AXIS_WIDTH} / ${CHART_HEIGHT}`,
            }}
            role="img"
            aria-hidden="true"
          >
            {PRICE_LABELS.map((price) => {
              const y = priceToY(price)
              return (
                <text
                  key={price}
                  x={CHART_WIDTH + 8}
                  y={y + 3}
                  fill="#9ca3af"
                  fontSize="11"
                  fontWeight="600"
                >
                  {formatUsd(price)}
                </text>
              )
            })}

            {candles.map((candle, index) => (
              <CandleStick
                key={`${candle.open}-${candle.close}-${candle.high}-${candle.low}`}
                candle={candle}
                index={index}
                visible={index < visibleCount}
              />
            ))}

            {visibleCount >= CANDLE_COUNT ? (
              <g
                style={{
                  opacity: showPriceLabel ? 1 : 0,
                  transition: "opacity 220ms ease-out",
                }}
              >
                <rect
                  x={CHART_WIDTH + 7}
                  y={currentPriceY - 9}
                  width={48}
                  height={18}
                  rx={4}
                  fill="#00EC97"
                />
                <text
                  x={CHART_WIDTH + 31}
                  y={currentPriceY + 3.5}
                  fill="#171717"
                  fontSize="11"
                  fontWeight="600"
                  textAnchor="middle"
                >
                  {formatUsd(FINAL_PRICE)}
                </text>
              </g>
            ) : null}
          </svg>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-2 p-3">
        <div className="rounded-lg bg-green-500/15 py-2.5 text-center font-semibold text-green-500 text-sm/5 tracking-tight">
          Long
        </div>
        <div className="rounded-lg bg-red-500/15 py-2.5 text-center font-semibold text-red-400 text-sm/5 tracking-tight">
          Short
        </div>
      </div>
    </div>
  )
}

export default TradingIllustration
